import { Api } from '@Services/api'
import {
    AuditEvent,
    Purchase,
    FinanceSummary,
    PurchasePayment,
    ProductionLinksResult,
    ProcessAttachmentsAggregate,
    PurchasePreparation,
    PurchaseProcess,
    PurchasingAttachment,
    PurchaseStatus,
    PurchasingStage,
    PurchasingBoard,
    PurchasingSupplier,
    SourceContext,
    SupplierQuote,
    SupplierQuoteStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import PurchasingDataSource from '../PurchasingDataSource'
import {
    CreatePurchaseProcessPayload,
    CreatePurchasingSupplierPayload,
    CreateSupplierQuotePayload,
    FetchPurchaseProcessesParams,
    PaginatedPurchaseProcesses,
    UpdateProcessAssigneePayload,
    UpdateProcessStagePayload,
    UpdateProcessStatusPayload,
    UpdatePurchaseProcessPayload,
    UpdatePurchasingSupplierPayload,
    UpdateSupplierQuotePayload,
    BatchCreationResult,
    CreateProcessBatchPayload,
    CreatePurchasePayload,
    UpdatePurchasePayload,
    CreatePurchasePaymentPayload,
    MarkPaymentPaidPayload,
    UpdatePurchasePaymentPayload,
    InitiateAttachmentPayload,
    UpdateAttachmentPayload,
} from './Entity/PurchaseProcess'

export class PurchasingAPI implements PurchasingDataSource {
    axios = new Api()

    async getProcesses(
        params: FetchPurchaseProcessesParams
    ): Promise<PaginatedPurchaseProcesses> {
        const { data } = await this.axios.get('/purchasing/processes', {
            params,
        })
        return data
    }

    async getProcessById(id: string): Promise<PurchaseProcess> {
        const { data } = await this.axios.get(`/purchasing/processes/${id}`)
        return data
    }

    async createProcess(
        payload: CreatePurchaseProcessPayload
    ): Promise<PurchaseProcess> {
        const { data } = await this.axios.post('/purchasing/processes', payload)
        return data
    }

    async updateProcess(
        id: string,
        payload: UpdatePurchaseProcessPayload
    ): Promise<PurchaseProcess> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${id}`,
            payload
        )
        return data
    }

    async updateProcessStatus(
        id: string,
        payload: UpdateProcessStatusPayload
    ): Promise<PurchaseProcess> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${id}/status`,
            payload
        )
        return data
    }

    async updateProcessStage(
        id: string,
        payload: UpdateProcessStagePayload
    ): Promise<PurchaseProcess> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${id}/stage`,
            payload
        )
        return data
    }

    async updateProcessAssignee(
        id: string,
        payload: UpdateProcessAssigneePayload
    ): Promise<PurchaseProcess> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${id}/assignee`,
            payload
        )
        return data
    }

    async getProcessAudit(id: string): Promise<AuditEvent[]> {
        const { data } = await this.axios.get(
            `/purchasing/processes/${id}/audit`
        )
        return data
    }

    async getProduction(processId: string): Promise<ProductionLinksResult> {
        const { data } = await this.axios.get(`/purchasing/processes/${processId}/production`)
        return data
    }

    async refreshProduction(processId: string): Promise<void> {
        await this.axios.post(`/purchasing/processes/${processId}/production/refresh`)
    }

    async confirmProduction(processId: string, productionId: string): Promise<void> {
        await this.axios.post(`/purchasing/processes/${processId}/production/${productionId}/confirm`)
    }

    async unlinkProduction(processId: string, productionId: string): Promise<void> {
        await this.axios.delete(`/purchasing/processes/${processId}/production/${productionId}`)
    }

    async getStages(): Promise<PurchasingStage[]> {
        const { data } = await this.axios.get('/purchasing/stages')
        return data
    }

    async getBoard(params: Record<string, string | number | undefined>): Promise<PurchasingBoard> {
        const { data } = await this.axios.get('/purchasing/processes/board', { params })
        return data
    }

    async getReportOverview(params: Record<string, string | undefined>): Promise<any> {
        const { data } = await this.axios.get('/purchasing/reports/overview', { params })
        return data
    }

    async createStage(name: string): Promise<PurchasingStage> {
        const { data } = await this.axios.post('/purchasing/stages', { name })
        return data
    }

    async updateStage(id: string, update: { name?: string; active?: boolean }): Promise<PurchasingStage> {
        const { data } = await this.axios.patch(`/purchasing/stages/${id}`, update)
        return data
    }

    async moveStage(id: string, direction: 'up' | 'down'): Promise<PurchasingStage[]> {
        const { data } = await this.axios.post(`/purchasing/stages/${id}/move`, { direction })
        return data
    }

    async getSourceContext(
        code?: string,
        externalId?: string
    ): Promise<SourceContext> {
        const { data } = await this.axios.get('/purchasing/source-context', {
            params: { code, externalId },
        })
        return data
    }

    async getQuotes(processId: string): Promise<SupplierQuote[]> {
        const { data } = await this.axios.get(
            `/purchasing/processes/${processId}/quotes`
        )
        return data
    }

    async createQuote(
        processId: string,
        payload: CreateSupplierQuotePayload
    ): Promise<SupplierQuote> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/quotes`,
            payload
        )
        return data
    }

    async updateQuote(
        processId: string,
        quoteId: string,
        payload: UpdateSupplierQuotePayload
    ): Promise<SupplierQuote> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${processId}/quotes/${quoteId}`,
            payload
        )
        return data
    }

    async updateQuoteStatus(
        processId: string,
        quoteId: string,
        status: SupplierQuoteStatus
    ): Promise<SupplierQuote> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${processId}/quotes/${quoteId}/status`,
            { status }
        )
        return data
    }

    async selectQuote(
        processId: string,
        quoteId: string
    ): Promise<PurchaseProcess> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/quotes/${quoteId}/select`
        )
        return data
    }

    async deleteQuote(processId: string, quoteId: string): Promise<void> {
        await this.axios.delete(
            `/purchasing/processes/${processId}/quotes/${quoteId}`
        )
    }

    async getPreparation(params: {
        budgetCode?: number
        externalId?: string
    }): Promise<PurchasePreparation> {
        const { data } = await this.axios.get(
            '/purchasing/processes/preparation',
            { params }
        )
        return data
    }

    async createBatch(
        payload: CreateProcessBatchPayload
    ): Promise<BatchCreationResult> {
        const { data } = await this.axios.post(
            '/purchasing/processes/batch',
            payload
        )
        return data
    }

    async getPurchase(processId: string): Promise<Purchase | null> {
        const { data } = await this.axios.get(
            `/purchasing/processes/${processId}/purchase`
        )
        return data
    }

    async createPurchase(
        processId: string,
        payload?: CreatePurchasePayload
    ): Promise<Purchase> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/purchase`,
            payload ?? {}
        )
        return data
    }

    async updatePurchase(
        processId: string,
        payload: UpdatePurchasePayload
    ): Promise<Purchase> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${processId}/purchase`,
            payload
        )
        return data
    }

    async updatePurchaseStatus(
        processId: string,
        status: PurchaseStatus
    ): Promise<Purchase> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${processId}/purchase/status`,
            { status }
        )
        return data
    }

    async getPurchaseFinance(processId: string) {
        const { data } = await this.axios.get(
            `/purchasing/processes/${processId}/purchase/payments`
        )
        return data as {
            summary: FinanceSummary
            payments: PurchasePayment[]
        }
    }

    async createPurchasePayment(
        processId: string,
        payload: CreatePurchasePaymentPayload
    ): Promise<PurchasePayment> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/purchase/payments`,
            payload
        )
        return data
    }

    async updatePurchasePayment(
        processId: string,
        paymentId: string,
        payload: UpdatePurchasePaymentPayload
    ): Promise<PurchasePayment> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${processId}/purchase/payments/${paymentId}`,
            payload
        )
        return data
    }

    async markPurchasePaymentPaid(
        processId: string,
        paymentId: string,
        payload: MarkPaymentPaidPayload
    ): Promise<PurchasePayment> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/purchase/payments/${paymentId}/pay`,
            payload
        )
        return data
    }

    async cancelPurchasePayment(
        processId: string,
        paymentId: string
    ): Promise<PurchasePayment> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/purchase/payments/${paymentId}/cancel`
        )
        return data
    }

    async getProcessAttachments(
        processId: string
    ): Promise<ProcessAttachmentsAggregate> {
        const { data } = await this.axios.get(
            `/purchasing/processes/${processId}/attachments`
        )
        return data
    }

    async getEntityAttachments(
        processId: string,
        entityType: string,
        entityId: string
    ): Promise<PurchasingAttachment[]> {
        const { data } = await this.axios.get(
            `/purchasing/processes/${processId}/attachments`,
            { params: { entityType, entityId } }
        )
        return data
    }

    async initiateAttachment(
        processId: string,
        payload: InitiateAttachmentPayload
    ): Promise<PurchasingAttachment> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/attachments/initiate`,
            payload
        )
        return data
    }

    async completeAttachment(
        processId: string,
        attachmentId: string
    ): Promise<PurchasingAttachment> {
        const { data } = await this.axios.post(
            `/purchasing/processes/${processId}/attachments/${attachmentId}/complete`
        )
        return data
    }

    async updateAttachment(
        processId: string,
        attachmentId: string,
        payload: UpdateAttachmentPayload
    ): Promise<PurchasingAttachment> {
        const { data } = await this.axios.patch(
            `/purchasing/processes/${processId}/attachments/${attachmentId}`,
            payload
        )
        return data
    }

    async deleteAttachment(
        processId: string,
        attachmentId: string
    ): Promise<{ removed: boolean; storageRemoved: boolean }> {
        const { data } = await this.axios.delete(
            `/purchasing/processes/${processId}/attachments/${attachmentId}`
        )
        return data
    }

    async getSuppliers(params?: {
        active?: boolean
        search?: string
    }): Promise<PurchasingSupplier[]> {
        const { data } = await this.axios.get('/supplier', { params })
        return data
    }

    async searchSuppliers(query: string): Promise<PurchasingSupplier[]> {
        const { data } = await this.axios.get(`/supplier/search/${query}`)
        return data
    }

    async createSupplier(
        payload: CreatePurchasingSupplierPayload
    ): Promise<PurchasingSupplier> {
        const { data } = await this.axios.post('/supplier', payload)
        return data
    }

    async updateSupplier(
        id: string,
        payload: UpdatePurchasingSupplierPayload
    ): Promise<PurchasingSupplier> {
        const { data } = await this.axios.put(`/supplier/${id}`, payload)
        return data
    }

    getSource(): string {
        return this.axios.getSource()
    }
}
